import { api, type NovelItem, type PluginInfo, type FiltersDef, type FilterDef } from '../api';
import { renderNav } from '../nav';
import { getCurrentRole, canAccessByHint } from '../auth';
import { canAccess } from '../roles';
import { coverEl, initials, toast, qs, workHref, clampTwoLines, refreshPrettyHrefs, sourceHref } from '../utils';
import { warmSlugCache } from '../pretty-cache';
import { GUEST_SOURCE_ID } from '../nav';
import { HIDDEN_PLUGIN_ID, rememberGuestTab } from '../pretty-path';
import { fetchBanners, mountBanner, type BannerController } from '../banner';

const SRC_COLORS = ['#3ba4ff', '#e8845a', '#5ac98a', '#c96de8', '#e8d95a', '#e85a5a'];

function colorFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return SRC_COLORS[h % SRC_COLORS.length];
}

function escapeHtml(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

// ---------------- одноразове повідомлення при першому вході в джерело ----------------

/** Повідомлення, які показуємо один раз (перший вхід у конкретне джерело) у
 * вигляді модалки поверх сторінки джерела. Сам текст і перемикач
 * увімкнено/вимкнено керуються з панелі /b30508f3c06b2d6e9f8a6e2c6e7f3e6b/
 * (категорія "Смаколики") — тут лише запит і показ; редагування — на сервері
 * (src/lib/source-notice.ts, GET /api/plugins/:id/notice). */
interface SourceNotice {
  enabled: boolean;
  title?: string;
  lines?: string[];
}

function sourceNoticeSeenKey(pluginId: string): string {
  return `yomukoma:source-notice-seen:${pluginId}`;
}

/** Показує модалку з повідомленням джерела, якщо сервер каже, що воно
 * увімкнене (панель підтримки), і користувач її ще не закривав (позначка в
 * localStorage — переживає перезавантаження сторінки й рестарт сервера,
 * на відміну від sessionStorage, який тут скидається щоразу). */
async function maybeShowSourceFirstVisitNotice(pluginId: string): Promise<void> {
  const seenKey = sourceNoticeSeenKey(pluginId);
  try {
    if (localStorage.getItem(seenKey)) return;
  } catch {
    /* localStorage недоступний — краще показати повідомлення зайвий раз, ніж жодного разу */
  }

  let notice: SourceNotice;
  try {
    const res = await fetch(`/api/plugins/${encodeURIComponent(pluginId)}/notice`);
    notice = res.ok ? await res.json() : { enabled: false };
  } catch {
    return; // мережева помилка — краще нічого не показати, ніж зламати сторінку джерела
  }
  if (!notice.enabled || !notice.title || !notice.lines?.length) return;

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay-center';
  overlay.innerHTML = `
    <div class="center-modal">
      <h3 class="modal-title">${escapeHtml(notice.title)}</h3>
      <div class="modal-details">${notice.lines.map((line) => `<p>${escapeHtml(line)}</p>`).join('')}</div>
      <div class="modal-actions">
        <button type="button" id="source-notice-close" class="btn btn-accent">Зрозуміло</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => {
    try {
      localStorage.setItem(seenKey, '1');
    } catch {
      /* якщо не вдалось зберегти — модалка просто з'явиться знову наступного разу, не критично */
    }
    overlay.remove();
  };

  (overlay.querySelector('#source-notice-close') as HTMLButtonElement).addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
}

// ---------------- збереження фільтрів і позиції скролу для сторінки джерела ----------------

/** Те, що зберігаємо для однієї сторінки джерела (browse.html?plugin=X), щоб
 * при поверненні зі сторінки твору показати той самий список на тому ж
 * місці, з тими самими фільтрами — без повторного запиту на сервер. */
interface SavedDetailState {
  values: Record<string, unknown>;
  mode: 'popular' | 'search';
  query: string;
  page: number;
  exhausted: boolean;
  scrollY: number;
  novels: NovelItem[];
  lastSignature: string;
}

function detailStateKey(pluginId: string): string {
  return `browse:state:${pluginId}`;
}

function loadDetailState(pluginId: string): SavedDetailState | null {
  try {
    const raw = sessionStorage.getItem(detailStateKey(pluginId));
    return raw ? (JSON.parse(raw) as SavedDetailState) : null;
  } catch {
    return null;
  }
}

function saveDetailState(pluginId: string, state: SavedDetailState) {
  try {
    sessionStorage.setItem(detailStateKey(pluginId), JSON.stringify(state));
  } catch {
    /* сховище недоступне/переповнене (напр. приватний режим) — просто не зберігаємо позицію */
  }
}

function clearDetailState(pluginId: string) {
  try {
    sessionStorage.removeItem(detailStateKey(pluginId));
  } catch {
    /* ignore */
  }
}

/**
 * Чи прийшли саме "назад" зі сторінки твору/аніме ЦЬОГО Ж джерела — лише
 * тоді має сенс відновлювати збережені фільтри й позицію скролу. Будь-який
 * інший вхід на сторінку джерела (зі списку джерел — в т.ч. стрілкою
 * "назад" з цієї ж сторінки джерела, з бібліотеки після прочитаного
 * розділу, з нижньої навігації, пряме посилання тощо) має починати перегляд
 * заново: дефолтні фільтри, скрол на початку.
 */
function isReturningFromWorkPage(pluginId: string): boolean {
  if (!document.referrer) return false;
  try {
    const ref = new URL(document.referrer);
    if (['/novel', '/novel.html', '/anime', '/anime.html'].includes(ref.pathname)) {
      return ref.searchParams.get('plugin') === pluginId;
    }
    // Коротке посилання /<pluginId>/<слаг> — джерело прямо в самому шляху.
    const segs = ref.pathname.split('/').filter(Boolean);
    // Для прихованого джерела сторінка твору — /title/<слаг> (див. pretty-path.ts).
    if (pluginId === HIDDEN_PLUGIN_ID && segs.length === 2 && segs[0] === 'title') return true;
    return segs.length === 2 && segs[0] === pluginId;
  } catch {
    return false;
  }
}

type XCheckboxValue = { included: string[]; excluded: string[] };

/** Спільний контекст для рендеру полів (використовується і для сесійних
 * фільтрів у джерелі, і для persisted-налаштувань джерела). */
interface FieldsCtx {
  defs: FiltersDef;
  values: Record<string, unknown>;
  tagsMode: 'solo' | 'multiple';
}

const OVERLAY_TYPES = new Set(['Picker', 'Checkbox', 'XCheckbox']);

function pickerLabelFor(def: FilterDef, value: string): string {
  return def.options?.find((o) => o.value === value)?.label ?? (value || def.options?.[0]?.label || '');
}

/** Чи можна в цьому конкретному Picker-полі обрати кілька значень —
 * загальний tagsMode плагіна, АЛЕ з можливістю примусово вимкнути для
 * окремого поля через def.solo (напр. сортування/напрямок завжди одиничні). */
function isMultiPicker(ctx: FieldsCtx, def: FilterDef): boolean {
  return ctx.tagsMode === 'multiple' && !def.solo;
}

function updateFieldButtonText(btn: HTMLButtonElement, ctx: FieldsCtx, key: string, def: FilterDef) {
  const textEl = btn.querySelector('.picker-value-text') as HTMLElement;

  if (def.type === 'Picker') {
    if (isMultiPicker(ctx, def)) {
      const values = String(ctx.values[key] ?? '')
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
      textEl.textContent = values.length ? values.map((v) => pickerLabelFor(def, v)).join(', ') : pickerLabelFor(def, '');
    } else {
      textEl.textContent = pickerLabelFor(def, String(ctx.values[key] ?? ''));
    }
    return;
  }

  if (def.type === 'Checkbox') {
    const arr = Array.isArray(ctx.values[key]) ? (ctx.values[key] as string[]) : [];
    if (!arr.length) textEl.textContent = 'Не обрано';
    else if (arr.length <= 2) textEl.textContent = arr.map((v) => pickerLabelFor(def, v)).join(', ');
    else textEl.textContent = `Обрано: ${arr.length}`;
    return;
  }

  // XCheckbox — тристанове включення/виключення
  const val = (ctx.values[key] ?? {}) as XCheckboxValue;
  const inc = val.included?.length ?? 0;
  const exc = val.excluded?.length ?? 0;
  if (!inc && !exc) textEl.textContent = 'Без обмежень';
  else {
    const parts: string[] = [];
    if (inc) parts.push(`включено: ${inc}`);
    if (exc) parts.push(`виключено: ${exc}`);
    textEl.textContent = parts.join(', ');
  }
}

function openOptionsOverlay(ctx: FieldsCtx, key: string, def: FilterDef, btn: HTMLButtonElement) {
  const overlay = document.getElementById('picker-overlay') as HTMLElement;
  const title = document.getElementById('picker-title') as HTMLElement;
  const list = document.getElementById('picker-list') as HTMLElement;
  const actions = document.getElementById('picker-actions') as HTMLElement;
  const applyBtn = document.getElementById('picker-apply') as HTMLButtonElement;
  const closeBtn = document.getElementById('picker-close') as HTMLElement;

  title.textContent = def.label;
  const options = def.options ?? [];

  const CHECK_SVG =
    '<svg class="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
  const CROSS_SVG =
    '<svg class="check cross" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  if (def.type === 'Picker') {
    const multi = isMultiPicker(ctx, def);
    const tempSelected = new Set(
      String(ctx.values[key] ?? '')
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean),
    );
    function renderList() {
      list.innerHTML = '';
      for (const o of options) {
        const isSelected = multi ? tempSelected.has(o.value) : ctx.values[key] === o.value;
        const r = document.createElement('div');
        r.className = 'migrate-plugin-row picker-option' + (isSelected ? ' selected' : '');
        r.innerHTML = `<span>${escapeHtml(o.label)}</span>${isSelected ? CHECK_SVG : ''}`;
        r.addEventListener('click', () => {
          if (multi) {
            if (tempSelected.has(o.value)) tempSelected.delete(o.value);
            else tempSelected.add(o.value);
            renderList();
          } else {
            ctx.values[key] = o.value;
            updateFieldButtonText(btn, ctx, key, def);
            overlay.style.display = 'none';
          }
        });
        list.appendChild(r);
      }
    }
    actions.style.display = multi ? 'flex' : 'none';
    applyBtn.onclick = () => {
      ctx.values[key] = Array.from(tempSelected).join(',');
      updateFieldButtonText(btn, ctx, key, def);
      overlay.style.display = 'none';
    };
    renderList();
  } else if (def.type === 'Checkbox') {
    const existing = Array.isArray(ctx.values[key]) ? (ctx.values[key] as string[]) : [];
    const tempSelected = new Set(existing);
    function renderList() {
      list.innerHTML = '';
      for (const o of options) {
        const isSelected = tempSelected.has(o.value);
        const r = document.createElement('div');
        r.className = 'migrate-plugin-row picker-option' + (isSelected ? ' selected' : '');
        r.innerHTML = `<span>${escapeHtml(o.label)}</span>${isSelected ? CHECK_SVG : ''}`;
        r.addEventListener('click', () => {
          if (tempSelected.has(o.value)) tempSelected.delete(o.value);
          else tempSelected.add(o.value);
          renderList();
        });
        list.appendChild(r);
      }
    }
    actions.style.display = 'flex';
    applyBtn.onclick = () => {
      ctx.values[key] = Array.from(tempSelected);
      updateFieldButtonText(btn, ctx, key, def);
      overlay.style.display = 'none';
    };
    renderList();
  } else {
    // XCheckbox — тап циклічно перемикає: нейтрально → включити → виключити → нейтрально
    const existing = (ctx.values[key] ?? {}) as XCheckboxValue;
    const tempIncluded = new Set(existing.included ?? []);
    const tempExcluded = new Set(existing.excluded ?? []);
    function stateOf(v: string): 'none' | 'inc' | 'exc' {
      if (tempIncluded.has(v)) return 'inc';
      if (tempExcluded.has(v)) return 'exc';
      return 'none';
    }
    function renderList() {
      list.innerHTML = '';
      for (const o of options) {
        const st = stateOf(o.value);
        const r = document.createElement('div');
        r.className = 'migrate-plugin-row picker-option' + (st !== 'none' ? ' selected' : '') + (st === 'exc' ? ' excluded' : '');
        r.innerHTML = `<span>${escapeHtml(o.label)}</span>${st === 'inc' ? CHECK_SVG : st === 'exc' ? CROSS_SVG : ''}`;
        r.addEventListener('click', () => {
          const cur = stateOf(o.value);
          tempIncluded.delete(o.value);
          tempExcluded.delete(o.value);
          if (cur === 'none') tempIncluded.add(o.value);
          else if (cur === 'inc') tempExcluded.add(o.value);
          renderList();
        });
        list.appendChild(r);
      }
    }
    actions.style.display = 'flex';
    applyBtn.onclick = () => {
      ctx.values[key] = { included: Array.from(tempIncluded), excluded: Array.from(tempExcluded) };
      updateFieldButtonText(btn, ctx, key, def);
      overlay.style.display = 'none';
    };
    renderList();
  }

  closeBtn.onclick = () => {
    overlay.style.display = 'none';
  };
  overlay.onclick = (e) => {
    if (e.target === overlay) overlay.style.display = 'none';
  };
  overlay.style.display = 'flex';
}

/** Рендерить усі поля ctx.defs у container (Switch/Picker/Checkbox/XCheckbox/Text). */
function renderFields(container: HTMLElement, ctx: FieldsCtx) {
  container.innerHTML = '';
  for (const key of Object.keys(ctx.defs)) {
    const def = ctx.defs[key];
    const row = document.createElement('div');
    row.className = 'filter-row';

    if (def.type === 'Switch') {
      row.innerHTML = `
        <label class="switchrow">
          <span>${escapeHtml(def.label)}</span>
          <span class="toggle">
            <input type="checkbox" data-key="${key}" ${ctx.values[key] ? 'checked' : ''}>
            <span class="toggle-track"><span class="toggle-thumb"></span></span>
          </span>
        </label>`;
    } else if (OVERLAY_TYPES.has(def.type)) {
      row.innerHTML = `
        <div class="field-outline">
          <span class="field-label">${escapeHtml(def.label)}</span>
          <button type="button" class="field-outline-btn" data-key="${key}" data-picker="1">
            <span class="picker-value-text"></span>
            <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </button>
        </div>`;
    } else if (def.type === 'Range') {
      const min = def.min ?? 0;
      const max = def.max ?? 100;
      const step = def.step ?? 1;
      const cur = Array.isArray(ctx.values[key]) ? (ctx.values[key] as [number, number]) : [min, max];
      row.innerHTML = `
        <div class="field-outline field-range">
          <span class="field-label">${escapeHtml(def.label)}</span>
          <div class="range-values"><span class="rv-min">${cur[0]}</span> – <span class="rv-max">${cur[1]}</span></div>
          <div class="dual-range" data-key="${key}">
            <div class="dual-range-track"><div class="dual-range-fill"></div></div>
            <input type="range" class="dual-range-input dual-range-min" min="${min}" max="${max}" step="${step}" value="${cur[0]}">
            <input type="range" class="dual-range-input dual-range-max" min="${min}" max="${max}" step="${step}" value="${cur[1]}">
          </div>
        </div>`;
    } else {
      row.innerHTML = `
        <div class="field-outline">
          <span class="field-label">${escapeHtml(def.label)}</span>
          <input type="text" data-key="${key}" class="field-text" value="${escapeHtml(String(ctx.values[key] ?? ''))}">
        </div>`;
    }
    container.appendChild(row);

    if (OVERLAY_TYPES.has(def.type)) {
      const btn = row.querySelector('.field-outline-btn') as HTMLButtonElement;
      updateFieldButtonText(btn, ctx, key, def);
      btn.addEventListener('click', () => openOptionsOverlay(ctx, key, def, btn));
    } else if (def.type === 'Range') {
      setupRangeSlider(row, ctx, key, def);
    }
  }
}

/** Дві накладені <input type=range>, що керують одним значенням [min, max]
 * у ctx.values[key] — читаються напряму (без collectFields), бо оновлюються
 * "наживо" при кожному перетягуванні повзунка. */
function setupRangeSlider(row: HTMLElement, ctx: FieldsCtx, key: string, def: FilterDef) {
  const min = def.min ?? 0;
  const max = def.max ?? 100;
  const wrap = row.querySelector('.dual-range') as HTMLElement;
  const fill = row.querySelector('.dual-range-fill') as HTMLElement;
  const minInput = row.querySelector('.dual-range-min') as HTMLInputElement;
  const maxInput = row.querySelector('.dual-range-max') as HTMLInputElement;
  const rvMin = row.querySelector('.rv-min') as HTMLElement;
  const rvMax = row.querySelector('.rv-max') as HTMLElement;

  function paint() {
    const lo = Number(minInput.value);
    const hi = Number(maxInput.value);
    const span = max - min || 1;
    fill.style.left = `${((lo - min) / span) * 100}%`;
    fill.style.right = `${100 - ((hi - min) / span) * 100}%`;
    rvMin.textContent = String(lo);
    rvMax.textContent = String(hi);
    ctx.values[key] = [lo, hi];
  }

  minInput.addEventListener('input', () => {
    if (Number(minInput.value) > Number(maxInput.value)) minInput.value = maxInput.value;
    paint();
  });
  maxInput.addEventListener('input', () => {
    if (Number(maxInput.value) < Number(minInput.value)) maxInput.value = minInput.value;
    paint();
  });

  ctx.values[key] = [Number(minInput.value), Number(maxInput.value)];
  paint();
  void wrap;
}

/** Зчитує значення Switch/Text полів з DOM назад у ctx.values (Picker/Checkbox/XCheckbox
 * вже оновлюються напряму через шторку вибору). */
function collectFields(container: HTMLElement, ctx: FieldsCtx) {
  container.querySelectorAll<HTMLInputElement>('[data-key]:not([data-picker])').forEach((el) => {
    const key = el.dataset.key!;
    const def = ctx.defs[key];
    // Range тримає ctx.values[key] актуальним "наживо" (setupRangeSlider) —
    // тут його чіпати не треба (і не можна: data-key тут висить на
    // обгортці .dual-range, а не на самому <input>, тож el.value для неї
    // взагалі не має сенсу).
    if (!def || def.type === 'Range') return;
    ctx.values[key] = def.type === 'Switch' ? el.checked : el.value;
  });
}

export async function initBrowse() {
  const sourcesView = document.getElementById('sources-view') as HTMLElement;
  const detailView = document.getElementById('detail-view') as HTMLElement;
  const searchbarSources = document.getElementById('searchbar-sources') as HTMLElement;
  const searchbarDetail = document.getElementById('searchbar-detail') as HTMLElement;
  const filterFab = document.getElementById('filter-fab') as HTMLElement;
  const bottomnav = document.getElementById('bottomnav') as HTMLElement;

  // У гостя вкладка "Каталог" (nav.ts) відкриває джерело напряму (/plugin/<id>),
  // а не список джерел, — тому для гостя ця сторінка джерела є не побічним
  // екраном (куди раніше приходили лише через "Огляд" → клік по джерелу), а
  // основною вкладкою: нижню навігацію ховати не можна, інакше гість не зможе
  // перейти нікуди більше зі своєї єдиної загальнодоступної сторінки.
  // Нижнє меню малюємо ОДРАЗУ за підказкою (гість / гостьовий акаунт — режим каталогу), не чекаючи мережі:
  // раніше воно з'являлось лише після відповіді /api/auth/me, а на деяких телефонах — аж після першого руху.
  if (!canAccessByHint('overview')) {
    renderNav('browse');
    (document.getElementById('bottomnav') as HTMLElement | null)?.style.setProperty('display', 'flex');
  }
  // "Режим каталогу" (в коді — isGuest): гість без акаунта І гостьовий акаунт tier_1_subscriber бачать каталог
  // єдиного джерела замість Огляду (список усіх джерел — лише для звичайних акаунтів, право 'overview').
  const isGuest = !canAccess(await getCurrentRole(), 'overview');
  // Гість (без акаунта) завжди бачить каталог єдиного доступного джерела — навіть якщо
  // відкрив /browse без ?plugin=: список джерел розкрив би, що їх є кілька.
  const pluginId = qs('plugin') || (isGuest ? GUEST_SOURCE_ID : null);
  const isGuestCatalog = !!pluginId && isGuest;

  // Клас no-sidebar каже стилям (style.css), що бічної панелі на екрані немає, — інакше
  // :has(.bottomnav) лишає під неї відступ, і контент сторінки джерела зміщений від центру.
  // Для гостя на "Каталозі" бічна панель (нижня навігація) якраз є, тож клас не додаємо.
  document.body.classList.toggle('no-sidebar', !!pluginId && !isGuestCatalog);
  document.body.classList.toggle('guest-catalog', isGuestCatalog);

  if (pluginId) {
    if (isGuestCatalog) {
      renderNav('browse');
      bottomnav.style.display = 'flex';
    } else {
      bottomnav.style.display = 'none';
    }
    sourcesView.style.display = 'none';
    searchbarSources.style.display = 'none';
    detailView.style.display = 'block';
    searchbarDetail.style.display = 'flex';
    await initDetail(pluginId, isGuest);
  } else {
    renderNav('browse');
    bottomnav.style.display = 'flex';
    filterFab.style.display = 'none';
    detailView.style.display = 'none';
    searchbarDetail.style.display = 'none';
    sourcesView.style.display = 'block';
    searchbarSources.style.display = 'flex';
    await initSources();
  }
}

async function initSources() {
  const sourcesView = document.getElementById('sources-view') as HTMLElement;
  const list = document.getElementById('sources-list') as HTMLElement;
  const searchInput = document.getElementById('search-sources') as HTMLInputElement;
  const searchResultsView = document.getElementById('search-results-view') as HTMLElement;
  const grid = document.getElementById('search-results-grid') as HTMLElement;
  const typeTabsRow = document.getElementById('search-type-tabs-row') as HTMLElement;
  const typeTabs = document.getElementById('search-type-tabs') as HTMLElement;

  let plugins: PluginInfo[] = [];
  try {
    plugins = await api.plugins();
  } catch (err) {
    toast((err as Error).message);
  }

  const libraryKeys = new Set<string>();
  try {
    const libItems = await api.library();
    for (const it of libItems) libraryKeys.add(`${it.pluginId}|${it.path}`);
  } catch {
    /* ignore — позначки "у бібліотеці" не критичні для роботи сторінки */
  }

  // ---------------- список джерел: окремо "Новели" і "Манґи" ----------------

  function renderSourceRow(p: PluginInfo): HTMLElement {
    const row = document.createElement('div');
    row.className = 'srcrow';

    const link = document.createElement('a');
    link.href = sourceHref(p.id);
    link.style.textDecoration = 'none';
    link.style.color = 'inherit';
    link.style.display = 'flex';
    link.style.alignItems = 'center';
    link.style.gap = '0.75rem';
    link.style.flex = '1';
    link.style.minWidth = '0';

    const icon = document.createElement('div');
    icon.className = 'srcicon';
    if (p.icon) {
      const img = document.createElement('img');
      img.src = p.icon;
      img.alt = '';
      img.loading = 'lazy';
      img.addEventListener('error', () => {
        img.remove();
        icon.style.background = colorFor(p.id);
        icon.textContent = initials(p.name);
      });
      icon.appendChild(img);
    } else {
      icon.style.background = colorFor(p.id);
      icon.textContent = initials(p.name);
    }

    const info = document.createElement('div');
    info.style.minWidth = '0';
    const typeLabel = p.contentType === 'manga' ? 'Манґа' : p.contentType === 'anime' ? 'Аніме' : 'Новела';
    info.innerHTML = `<div class="srcname">${p.name}</div><div class="srcsub">${p.lang} • v${p.version} • ${typeLabel}</div>`;

    link.appendChild(icon);
    link.appendChild(info);
    row.appendChild(link);

    if (p.hasPreferences) {
      const gearBtn = document.createElement('button');
      gearBtn.className = 'srcgear';
      gearBtn.setAttribute('aria-label', `Налаштування ${p.name}`);
      gearBtn.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/></svg>';
      gearBtn.addEventListener('click', () => openSourceSettings(p));
      row.appendChild(gearBtn);
    }

    return row;
  }

  function renderSourceLists() {
    list.innerHTML = '';
    if (!plugins.length) {
      list.innerHTML = `<div class="empty">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>
        <div>Жодного джерела не підключено.<br>Додайте файл плагіна у src/plugins/sources.</div>
      </div>`;
      return;
    }

    // Новели, манґа й аніме — окремими списками, а не одним спільним.
    const novels = plugins.filter((p) => p.contentType === 'novel');
    const mangas = plugins.filter((p) => p.contentType === 'manga');
    const animes = plugins.filter((p) => p.contentType === 'anime');

    for (const [label, items] of [
      ['Новели', novels],
      ['Манґи', mangas],
      ['Аніме', animes],
    ] as const) {
      if (!items.length) continue;
      const labelEl = document.createElement('div');
      labelEl.className = 'section-label';
      labelEl.textContent = `${label} (${items.length})`;
      list.appendChild(labelEl);
      for (const p of items) list.appendChild(renderSourceRow(p));
    }
  }

  renderSourceLists();

  // ---------------- крос-джерельний пошук (шукає по всіх джерелах одразу, не фільтрує їх список) ----------------

  let searchContentType: 'novel' | 'manga' | 'anime' | '' = '';
  let query = '';
  let page = 1;
  let loadingMore = false;
  let exhausted = false;
  let requestToken = 0;
  let lastSignature = '';

  type SearchResult = NovelItem & { pluginId: string; pluginName: string };

  function signatureOf(items: SearchResult[]): string {
    return items.map((n) => `${n.pluginId}|${n.path}`).join('|');
  }

  function appendResults(items: SearchResult[]) {
    // Крос-джерельний пошук цілком може повернути той самий твір з того
    // самого джерела двічі на сусідніх сторінках (якщо джерело просто мало
    // менше результатів, ніж очікувалось) — прибираємо дублі за pluginId+path.
    const seen = new Set<string>(
      [...grid.querySelectorAll<HTMLAnchorElement>('a[data-key]')].map((a) => a.dataset.key!),
    );
    const forWarm: { pluginId: string; path: string; title: string }[] = [];
    for (const n of items) {
      const key = `${n.pluginId}|${n.path}`;
      if (seen.has(key)) continue;
      seen.add(key);
      forWarm.push({ pluginId: n.pluginId, path: n.path, title: n.name });

      const a = document.createElement('a');
      a.className = 'cardlink';
      a.dataset.key = key;
      const contentType = plugins.find((p) => p.id === n.pluginId)?.contentType;
      a.dataset.prettyPlugin = n.pluginId;
      a.dataset.prettyPath = n.path;
      if (contentType) a.dataset.prettyType = contentType;
      a.href = workHref(n.pluginId, n.path, contentType);
      const inLib = libraryKeys.has(key);
      const cover = coverEl(n.name, n.cover, n.pluginId + n.path, inLib);
      const title = document.createElement('div');
      title.className = 'cardtitle';
      title.textContent = n.name;
      const sub = document.createElement('div');
      sub.className = 'cardsub';
      sub.textContent = n.pluginName;
      a.appendChild(cover);
      a.appendChild(title);
      a.appendChild(sub);
      grid.appendChild(a);
      clampTwoLines(title);
    }
    if (forWarm.length) warmSlugCache(forWarm, () => refreshPrettyHrefs(grid));
  }

  async function loadFirstSearch(q: string) {
    const token = ++requestToken;
    query = q;
    page = 1;
    exhausted = false;
    grid.innerHTML = '<div class="empty">Пошук…</div>';
    try {
      const results = await api.searchAll(query, searchContentType, 1);
      if (token !== requestToken) return;
      grid.innerHTML = '';
      if (!results.length) {
        grid.innerHTML = '<div class="empty" style="grid-column:1/-1"><div>Нічого не знайдено.</div></div>';
        exhausted = true;
        return;
      }
      appendResults(results);
      lastSignature = signatureOf(results);
    } catch (err) {
      if (token !== requestToken) return;
      toast((err as Error).message);
      grid.innerHTML = '<div class="empty">Не вдалося виконати пошук.</div>';
    }
  }

  async function loadMoreSearch() {
    if (loadingMore || exhausted || !query) return;
    loadingMore = true;
    const nextPage = page + 1;
    const loader = document.createElement('div');
    loader.className = 'empty';
    loader.style.gridColumn = '1/-1';
    loader.textContent = 'Завантажую ще…';
    grid.appendChild(loader);
    try {
      const results = await api.searchAll(query, searchContentType, nextPage);
      loader.remove();
      const sig = signatureOf(results);
      if (!results.length || sig === lastSignature) {
        exhausted = true;
      } else {
        page = nextPage;
        lastSignature = sig;
        appendResults(results);
      }
    } catch (err) {
      loader.remove();
      console.error('[browse] Не вдалося довантажити результати пошуку:', err);
      exhausted = true;
    } finally {
      loadingMore = false;
    }
  }

  function nearBottom(): boolean {
    return window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 400;
  }
  window.addEventListener('scroll', () => {
    if (searchResultsView.style.display !== 'none' && nearBottom()) loadMoreSearch();
  });

  function showSearchMode(active: boolean) {
    sourcesView.style.display = active ? 'none' : '';
    searchResultsView.style.display = active ? '' : 'none';
    typeTabsRow.style.display = active ? 'flex' : 'none';
  }

  let searchTimer: number | undefined;
  searchInput.addEventListener('input', () => {
    window.clearTimeout(searchTimer);
    const q = searchInput.value.trim();
    showSearchMode(!!q);
    if (!q) return;
    searchTimer = window.setTimeout(() => loadFirstSearch(q), 400);
  });

  typeTabs.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('button[data-type]') as HTMLButtonElement | null;
    if (!btn) return;
    typeTabs.querySelectorAll('.category-tab').forEach((el) => el.classList.remove('active'));
    btn.classList.add('active');
    searchContentType = (btn.dataset.type as 'novel' | 'manga' | 'anime' | '') ?? '';
    if (query) loadFirstSearch(query);
  });
}

async function openSourceSettings(plugin: PluginInfo, onSaved?: () => void) {
  const overlay = document.getElementById('settings-overlay') as HTMLElement;
  const title = document.getElementById('settings-title') as HTMLElement;
  const body = document.getElementById('settings-body') as HTMLElement;
  const closeBtn = document.getElementById('settings-close') as HTMLElement;
  const saveBtn = document.getElementById('settings-save') as HTMLButtonElement;

  title.textContent = `Налаштування: ${plugin.name}`;
  body.innerHTML = '<div class="empty">Завантаження…</div>';
  overlay.style.display = 'flex';
  closeBtn.onclick = () => (overlay.style.display = 'none');
  overlay.onclick = (e) => {
    if (e.target === overlay) overlay.style.display = 'none';
  };

  let res;
  try {
    res = await api.pluginPreferences(plugin.id);
  } catch (err) {
    body.innerHTML = `<div class="empty">${escapeHtml((err as Error).message)}</div>`;
    return;
  }

  const ctx: FieldsCtx = { defs: res.preferences, values: { ...res.values }, tagsMode: plugin.tagsMode };
  renderFields(body, ctx);

  saveBtn.onclick = async () => {
    collectFields(body, ctx);
    saveBtn.disabled = true;
    saveBtn.textContent = 'Зберігаю…';
    try {
      await api.savePluginPreferences(plugin.id, ctx.values);
      toast('Налаштування збережено');
      overlay.style.display = 'none';
      onSaved?.();
    } catch (err) {
      toast((err as Error).message);
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Зберегти';
    }
  };
}

async function initDetail(pluginId: string, isGuest: boolean) {
  const backBtn = document.getElementById('back-btn') as HTMLAnchorElement;
  const siteLink = document.getElementById('site-link') as HTMLAnchorElement;
  const grid = document.getElementById('detail-grid') as HTMLElement;
  const searchInput = document.getElementById('search-detail') as HTMLInputElement;
  const filterFab = document.getElementById('filter-fab') as HTMLButtonElement;
  const filterPanel = document.getElementById('filter-panel') as HTMLElement;
  const filterBackdrop = document.getElementById('filter-backdrop') as HTMLElement;
  const filterBody = document.getElementById('filter-body') as HTMLElement;
  const filterApply = document.getElementById('filter-apply') as HTMLButtonElement;
  const filterReset = document.getElementById('filter-reset') as HTMLButtonElement;
  const seg = document.getElementById('seg') as HTMLElement;
  const bannerHost = document.getElementById('guest-banner') as HTMLElement;
  const filterFabCount = document.getElementById('filter-fab-count') as HTMLElement | null;

  // Каталог гостя: дві вкладки — "Останні оновлення" (адреса /) і "Усі твори" (адреса /titles, з
  // фільтрами). Вкладку визначає адреса: сервер кладе guestTab у window.__PRETTY__ (routes/pretty.ts).
  type GuestTab = 'updates' | 'name';
  let tab: GuestTab = 'updates';
  if (isGuest) {
    const injected = (window as unknown as { __PRETTY__?: { guestTab?: string } }).__PRETTY__?.guestTab;
    tab = injected === 'name' || location.pathname === '/titles' ? 'name' : 'updates';
  }
  /** Ключ збереженого стану (скрол/фільтри): у гостя окремо для кожної вкладки. */
  const stateId = () => (isGuest ? `${pluginId}:${tab}` : pluginId);

  // Гостю нема куди вести стрілку "назад" — список джерел (/browse) йому не
  // показуємо в навігації (nav.ts: вкладка "Каталог" веде сюди напряму), тож
  // саму кнопку ховаємо, а не лишаємо мертвим переходом у "чужу" частину
  // застосунку. .detail-searchbar має flex:1 у .detail-topbar (style.css),
  // тож пошуковий рядок сам розтягується на звільнене місце.
  if (isGuest) {
    backBtn.style.display = 'none';
  } else {
    backBtn.href = '/browse';
  }

  void maybeShowSourceFirstVisitNotice(pluginId);

  // Відновлюємо збережений стан лише якщо це справді повернення зі сторінки
  // твору цього ж джерела; інакше — прибираємо застарілий запис (щоб не
  // "воскрес" при випадковому збігу document.referrer пізніше) і починаємо
  // перегляд джерела заново.
  const savedState = isReturningFromWorkPage(pluginId) ? loadDetailState(stateId()) : null;
  if (!savedState) clearDetailState(stateId());

  // Стрілка "назад" веде до списку джерел — це свідомий вихід із джерела,
  // тож наступного разу фільтри й позицію показуємо знову з нуля.
  backBtn.addEventListener('click', () => clearDetailState(stateId()));

  let plugins: PluginInfo[] = [];
  try {
    plugins = await api.plugins();
  } catch {
    /* ignore */
  }
  const plugin = plugins.find((p) => p.id === pluginId);
  searchInput.placeholder = plugin ? `Пошук ${plugin.name}` : 'Пошук';
  siteLink.href = plugin?.site || '#';
  // Гостю замість глобуса — логотип сайту (public/icons/smakolykytl.svg). Малюємо його маскою, а не <img>,
  // щоб колір брався з теми (CSS: .globe-btn .src-glyph → var(--logo) через color на обгортці).
  if (isGuest && pluginId === HIDDEN_PLUGIN_ID) {
    siteLink.classList.add('has-icon');
    siteLink.innerHTML = '';
    const glyph = document.createElement('span');
    glyph.className = 'src-glyph';
    glyph.setAttribute('role', 'img');
    glyph.setAttribute('aria-label', plugin?.name ?? '');
    siteLink.appendChild(glyph);
  }

  const libraryKeys = new Set<string>();
  try {
    const libItems = await api.library();
    for (const it of libItems) libraryKeys.add(`${it.pluginId}|${it.path}`);
  } catch {
    /* ignore — позначки "у бібліотеці" не критичні для роботи сторінки */
  }

  function openFilterPanel() {
    document.body.classList.add('panel-open');
    filterPanel.classList.add('open');
    filterBackdrop.classList.add('open');
    filterFab.classList.add('hidden');
  }
  function closeFilterPanel() {
    document.body.classList.remove('panel-open');
    filterPanel.classList.remove('open');
    filterBackdrop.classList.remove('open');
    filterFab.classList.remove('hidden');
  }

  const ctx: FieldsCtx = { defs: {}, values: {}, tagsMode: plugin?.tagsMode ?? 'solo' };

  if (plugin?.hasFilters) {
    try {
      const res = await api.pluginFilters(pluginId);
      ctx.defs = res.filters;
      // Гість на "Усі твори": сортування одне — "За назвою", тож самого вибору сортування в панелі
      // фільтрів нема, а значення підставляємо примусово (для стрічки за оновленнями є вкладка
      // "Останні оновлення").
      const forceNameSort = isGuest && pluginId === HIDDEN_PLUGIN_ID;
      if (forceNameSort) delete ctx.defs.sortBy;
      for (const key of Object.keys(ctx.defs)) ctx.values[key] = ctx.defs[key].value;
      if (forceNameSort) ctx.values.sortBy = 'name';
      // Накладаємо збережені значення лише для полів, що досі існують у
      // визначенні фільтрів джерела (список могли й змінити на сервері).
      if (savedState) {
        for (const key of Object.keys(ctx.defs)) {
          if (key in savedState.values) ctx.values[key] = savedState.values[key];
        }
      }
      renderFields(filterBody, ctx);
      filterFab.style.display = 'flex';
    } catch (err) {
      console.error('Не вдалося завантажити фільтри:', err);
      filterFab.style.display = 'none';
    }
  } else {
    filterFab.style.display = 'none';
  }

  /** Скільки полів фільтра відрізняються від значень за замовчуванням — число на кнопці "Фільтр". */
  function updateFilterCount() {
    if (!filterFabCount) return;
    let n = 0;
    for (const key of Object.keys(ctx.defs)) {
      if (JSON.stringify(ctx.values[key]) !== JSON.stringify(ctx.defs[key].value)) n++;
    }
    filterFabCount.textContent = n ? String(n) : '';
    filterFabCount.classList.toggle('on', n > 0);
  }
  updateFilterCount();

  filterFab.addEventListener('click', () => {
    if (filterPanel.classList.contains('open')) closeFilterPanel();
    else openFilterPanel();
  });
  filterBackdrop.addEventListener('click', closeFilterPanel);

  filterReset.addEventListener('click', () => {
    for (const key of Object.keys(ctx.defs)) ctx.values[key] = ctx.defs[key].value;
    renderFields(filterBody, ctx);
    closeFilterPanel();
    updateFilterCount();
    searchInput.value = '';
    loadFirst('popular');
  });
  filterApply.addEventListener('click', () => {
    collectFields(filterBody, ctx);
    closeFilterPanel();
    updateFilterCount();
    searchInput.value = '';
    loadFirst('popular');
  });

  // ---------- грід новел + пагінація (довантаження при гортанні вниз) ----------
  let mode: 'popular' | 'search' = savedState?.mode ?? 'popular';
  let query = savedState?.query ?? '';
  let page = savedState?.page ?? 1;
  let loadingMore = false;
  let exhausted = savedState?.exhausted ?? false;
  let requestToken = 0;
  let lastSignature = savedState?.lastSignature ?? '';
  // Повний список уже завантажених новел (усіх сторінок) — потрібен, щоб
  // зберігати позицію скролу разом зі списком: без нього після повернення
  // довелось би або перезавантажувати з першої сторінки (губимо скрол), або
  // вгадувати, скільки сторінок довантажити, щоб скрол співпав.
  let currentNovels: NovelItem[] = savedState?.novels ? [...savedState.novels] : [];

  if (mode === 'search') searchInput.value = query;

  function signatureOf(novels: NovelItem[]): string {
    return novels.map((n) => n.path).join('|');
  }

  /** Зберігає поточний стан (фільтри, режим/запит пошуку, сторінку, вже
   * завантажені новели й позицію скролу) — щоб при поверненні зі сторінки
   * твору відновити список і скрол без нового запиту. Викликається як
   * одразу після кожного (пере)завантаження списку, так і безпосередньо
   * перед переходом на сторінку твору (pagehide/клік по картці) — щоб
   * зафіксувати саме той scrollY, з якого користувач пішов. */
  function persistState() {
    saveDetailState(stateId(), {
      values: { ...ctx.values },
      mode,
      query,
      page,
      exhausted,
      scrollY: window.scrollY,
      novels: currentNovels,
      lastSignature,
    });
  }

  function appendNovels(novels: NovelItem[]) {
    for (const n of novels) {
      const a = document.createElement('a');
      a.className = 'cardlink';
      a.dataset.prettyPlugin = pluginId;
      a.dataset.prettyPath = n.path;
      if (plugin?.contentType) a.dataset.prettyType = plugin.contentType;
      a.href = workHref(pluginId, n.path, plugin?.contentType);
      a.addEventListener('click', persistState);
      const inLib = libraryKeys.has(`${pluginId}|${n.path}`);
      const cover = coverEl(n.name, n.cover, pluginId + n.path, inLib);
      const title = document.createElement('div');
      title.className = 'cardtitle';
      title.textContent = n.name;
      a.appendChild(cover);
      a.appendChild(title);
      grid.appendChild(a);
      clampTwoLines(title);
    }
    warmSlugCache(
      novels.map((n) => ({ pluginId, path: n.path, title: n.name })),
      () => refreshPrettyHrefs(grid),
    );
    trimToFullRows();
  }

  /**
   * "Останні оновлення" в гостя: показуємо не більше N творів (N задається в панелі підтримки:
   * окремо для ПК і для телефона; 0 — без обмеження). ПК = той самий широкий макет, що й у CSS
   * (@media min-width:900px і min-height:540px). Решта карток ховається, а не видаляється.
   */
  let updatesLimit = { desktop: 8, mobile: 9 };
  const desktopMq = window.matchMedia('(min-width: 900px) and (min-height: 540px)');
  function currentUpdatesLimit(): number {
    if (!isGuest || tab !== 'updates' || mode === 'search') return 0;
    return desktopMq.matches ? updatesLimit.desktop : updatesLimit.mobile;
  }
  function trimToFullRows() {
    if (!isGuest) return;
    const cards = Array.from(grid.querySelectorAll<HTMLElement>('a.cardlink'));
    const limit = currentUpdatesLimit();
    const visible = limit > 0 ? Math.min(cards.length, limit) : cards.length;
    cards.forEach((c, i) => {
      c.style.display = i < visible ? '' : 'none';
    });
  }

  async function fetchNovels(p: number): Promise<NovelItem[]> {
    if (mode === 'search') return api.search(pluginId, query, p);
    // Гість: "Останні оновлення" — стрічка за датою оновлення (без фільтрів), "Усі твори" — каталог з фільтрами.
    if (isGuest) {
      return tab === 'updates'
        ? api.popular(pluginId, p, true)
        : api.popular(pluginId, p, false, plugin?.hasFilters ? ctx.values : undefined);
    }
    return api.popular(pluginId, p, false, plugin?.hasFilters ? ctx.values : undefined);
  }

  // Банер каталогу гостя (див. нижче "вкладки каталогу гостя + банер"); loadFirst оновлює його видимість
  // (під час пошуку банера нема), саму логіку присвоєно пізніше.
  let banner: BannerController | null = null;
  let bannerShowOn: 'updates' | 'all' = 'updates';
  let syncBanner: () => void = () => {};

  async function loadFirst(newMode: 'popular' | 'search', newQuery = '') {
    const token = ++requestToken;
    mode = newMode;
    syncBanner();
    query = newQuery;
    page = 1;
    exhausted = false;
    currentNovels = [];
    grid.innerHTML = `<div class="empty">${mode === 'search' ? 'Пошук…' : 'Завантаження…'}</div>`;
    try {
      const novels = await fetchNovels(1);
      if (token !== requestToken) return;
      grid.innerHTML = '';
      if (!novels.length) {
        grid.innerHTML = '<div class="empty" style="grid-column:1/-1"><div>Нічого не знайдено.</div></div>';
        exhausted = true;
        persistState();
        return;
      }
      appendNovels(novels);
      currentNovels = novels;
      lastSignature = signatureOf(novels);
      persistState();
    } catch (err) {
      if (token !== requestToken) return;
      toast((err as Error).message);
      grid.innerHTML = '<div class="empty">Не вдалося завантажити список.</div>';
    }
  }

  async function loadMore() {
    if (loadingMore || exhausted) return;
    if (currentUpdatesLimit() > 0) return; // "Останні оновлення" з обмеженням: більше не підвантажуємо
    loadingMore = true;
    const nextPage = page + 1;
    const loader = document.createElement('div');
    loader.className = 'empty';
    loader.id = 'grid-loading';
    loader.style.gridColumn = '1/-1';
    loader.textContent = 'Завантажую ще…';
    grid.appendChild(loader);
    try {
      const novels = await fetchNovels(nextPage);
      loader.remove();
      const sig = signatureOf(novels);
      if (!novels.length || sig === lastSignature) {
        exhausted = true;
      } else {
        page = nextPage;
        lastSignature = sig;
        appendNovels(novels);
        currentNovels = currentNovels.concat(novels);
        persistState();
      }
    } catch (err) {
      loader.remove();
      console.error('[browse] Не вдалося довантажити сторінку:', err);
      exhausted = true;
    } finally {
      loadingMore = false;
    }
  }

  function nearBottom(): boolean {
    return window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 400;
  }
  window.addEventListener('scroll', () => {
    if (nearBottom()) loadMore();
  });
  // Останній шанс зафіксувати актуальний scrollY — саме в момент, коли
  // сторінка ховається (перехід на сторінку твору, згортання вкладки і т.п.),
  // а не лише після кожного довантаження списку.
  window.addEventListener('pagehide', persistState);

  let searchTimer: number | undefined;
  searchInput.addEventListener('input', () => {
    window.clearTimeout(searchTimer);
    const q = searchInput.value.trim();
    searchTimer = window.setTimeout(() => {
      loadFirst(q ? 'search' : 'popular', q);
    }, 400);
  });

  // ---------- вкладки каталогу гостя + банер ----------
  /** Банер видно лише на дозволених вкладках і не під час пошуку. */
  syncBanner = () => {
    if (!banner) return;
    const visible = (bannerShowOn === 'all' || tab === 'updates') && mode !== 'search';
    bannerHost.style.display = visible ? '' : 'none';
    banner.setActive(visible);
  };
  function paintTab() {
    rememberGuestTab(tab);
    document.body.dataset.mode = tab;
    seg.dataset.mode = tab;
    seg.querySelectorAll<HTMLButtonElement>('.seg-btn').forEach((b) => b.classList.toggle('active', b.dataset.mode === tab));
  }

  function switchTab(next: GuestTab, updateUrl: boolean) {
    if (next === tab) return;
    persistState(); // зберігаємо скрол/список тієї вкладки, з якої йдемо
    tab = next;
    paintTab();
    if (updateUrl) history.pushState(null, '', tab === 'name' ? '/titles' : '/');
    closeFilterPanel();
    searchInput.value = '';
    window.scrollTo(0, 0);
    void loadFirst('popular');
  }

  if (isGuest) {
    seg.style.display = '';
    paintTab();
    seg.querySelectorAll<HTMLButtonElement>('.seg-btn').forEach((b) => {
      b.addEventListener('click', () => switchTab(b.dataset.mode as GuestTab, true));
    });
    window.addEventListener('resize', trimToFullRows);
    // Кількість "Останніх оновлень" — з панелі підтримки (типово 8 на ПК і 9 на телефоні).
    void fetch('/api/guest-catalog')
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (!cfg) return;
        updatesLimit = { desktop: Number(cfg.updatesDesktop) || 0, mobile: Number(cfg.updatesMobile) || 0 };
        trimToFullRows();
      })
      .catch(() => {});
    // Кнопки браузера "назад/вперед" між / і /titles.
    window.addEventListener('popstate', () => switchTab(location.pathname === '/titles' ? 'name' : 'updates', false));
    // Банер: підвантажуємо паралельно зі списком; нема налаштованих слайдів → нічого не показуємо.
    void fetchBanners().then((cfg) => {
      banner = mountBanner(bannerHost, cfg);
      if (banner) {
        bannerShowOn = cfg.showOn === 'all' ? 'all' : 'updates';
        syncBanner();
      }
    });
  }

  if (savedState && currentNovels.length) {
    // Малюємо вже завантажений список одразу (без нового запиту) і лише
    // потім прокручуємо до збереженої позиції — інакше scrollTo спрацює до
    // того, як картки реально потраплять у DOM, і скрол не застосується.
    appendNovels(currentNovels);
    requestAnimationFrame(() => window.scrollTo(0, savedState.scrollY));
  } else {
    await loadFirst('popular');
  }
}
